import { Link } from "react-router-dom";

const COVER_ART = "https://static.readdy.ai/image/f9f32838a9120faa908a5852ba765d83/f8910ea58b264f83bfa3194cce1f37cf.png";

export default function Hero() {
  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #3b1505 0%, #6b2a08 35%, #92400e 70%, #a85510 100%)",
      }}
    >
      {/* Subtle texture overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 80% 50%, #f59e0b 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, #d97706 0%, transparent 50%)",
        }}
      />

      {/* Decorative rings */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-amber-600/10 translate-x-1/4 pointer-events-none" />
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-amber-600/5 translate-x-1/3 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* Left — text */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/35 rounded-full px-4 py-1.5 mb-8">
              <span className="w-2 h-2 flex items-center justify-center">
                <span className="block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              </span>
              <span className="font-sans text-xs font-semibold tracking-widest uppercase text-amber-300">
                New Episodes Every Monday
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-7">
              AI Is Changing{" "}
              <em className="text-amber-400 not-italic">Everything.</em>
              <br />
              Let&apos;s Navigate It{" "}
              <span className="relative inline-block">
                Together.
                <span
                  className="absolute -bottom-1 left-0 w-full h-0.5 rounded-full bg-amber-500/50"
                />
              </span>
            </h1>

            {/* Subheadline */}
            <p className="font-sans text-base md:text-lg text-amber-100/75 leading-relaxed max-w-xl mb-10">
              The Humble Machine is a podcast for curious, working people who want an honest, encouraging, and grounded conversation about one of the most powerful forces shaping our world &mdash; Artificial Intelligence.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link
                to="/episodes"
                className="inline-flex items-center justify-center gap-2.5 bg-amber-500 hover:bg-amber-400 text-amber-950 font-sans font-bold text-sm px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-800/30">
                  <i className="ri-play-fill text-amber-900 text-xs" />
                </span>
                Listen to Episode 1
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 border border-amber-400/50 text-amber-200 hover:border-amber-300 hover:text-amber-100 hover:bg-amber-800/20 font-sans font-semibold text-sm px-8 py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Learn More
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </Link>
            </div>

            {/* Platform row */}
            <div className="flex items-center gap-5 mb-10">
              <span className="font-sans text-xs text-amber-400/60 tracking-widest uppercase">
                Listen on
              </span>
              {[
                { icon: "ri-spotify-fill", label: "Spotify", href: "https://podcasters.spotify.com/pod/show/rich" },
                { icon: "ri-apple-fill", label: "Apple Podcasts", href: "#" },
                { icon: "ri-google-play-fill", label: "Google Podcasts", href: "#" },
              ].map((p) => (
                <a
                  key={p.label}
                  href={p.href}
                  rel="nofollow"
                  aria-label={p.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100/10 hover:bg-amber-100/20 transition-colors cursor-pointer"
                >
                  <i className={`${p.icon} text-amber-200 text-lg`} />
                </a>
              ))}
            </div>

            {/* Podcast stats strip */}
            <div className="flex items-center gap-0 divide-x divide-amber-600/30 bg-amber-500/10 border border-amber-500/20 rounded-2xl overflow-hidden">
              {[
                { value: "1", label: "Episodes" },
                { value: "0", label: "Monthly Listeners" },
                { value: "0", label: "Avg Rating" },
                { value: "3", label: "Platforms" },
              ].map((stat) => (
                <div key={stat.label} className="flex-1 flex flex-col items-center py-3 px-2">
                  <span className="font-serif font-bold text-amber-400 text-base leading-none mb-0.5">
                    {stat.value}
                  </span>
                  <span className="font-sans text-xs text-amber-300/60 whitespace-nowrap">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — cover art */}
          <div className="flex items-center justify-center lg:justify-end">
            <div className="relative">
              {/* Glow behind image */}
              <div className="absolute inset-0 rounded-3xl bg-amber-500/20 blur-3xl scale-90 translate-y-4" />

              {/* Cover art */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={COVER_ART}
                  alt="The Humble Machine Podcast Cover Art"
                  className="w-72 h-72 md:w-80 md:h-80 lg:w-96 lg:h-96 object-cover"
                />
                {/* Subtle border overlay */}
                <div className="absolute inset-0 rounded-3xl ring-1 ring-amber-400/20" />
              </div>

              {/* Floating listener count */}
              <div className="absolute -left-6 bottom-8 bg-white/10 backdrop-blur-md border border-amber-400/25 rounded-2xl px-5 py-3.5 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-400/20">
                  <i className="ri-headphone-line text-amber-300 text-base" />
                </div>
                <div>
                  <p className="font-sans font-bold text-cream text-sm leading-none mb-0.5">
                    0
                  </p>
                  <p className="font-sans text-xs text-amber-300/70 leading-none">
                    Monthly Listeners
                  </p>
                </div>
              </div>

              {/* Floating rating */}
              <div className="absolute -right-4 top-8 bg-white/10 backdrop-blur-md border border-amber-400/25 rounded-2xl px-4 py-3 flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[0,1,2,3,4].map((i) => (
                    <span key={i} className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-star-line text-amber-400/50 text-xs" />
                    </span>
                  ))}
                </div>
                <p className="font-sans text-xs font-semibold text-cream">0</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-cream/10 to-transparent pointer-events-none" />
    </section>
  );
}
